import { Injectable } from '@angular/core';
import { HelperService } from '../../servicios/helper.service';

/**
 * Servicio para generar íconos de vehículos dinámicamente usando Canvas.
 *
 * Este servicio replica la funcionalidad de OpenLayers donde se podían aplicar
 * colores a imágenes PNG. Como Mapbox no soporta esto nativamente, generamos
 * las imágenes compuestas dinámicamente.
 *
 * Sistema de capas (de abajo hacia arriba):
 * 1. Círculo de selección (solo si seleccionado) - colorEstado aclarado
 * 2. Fondo central - color de flota o blanco
 * 3. Anillo de orientación - colorEstado (rotable)
 * 4. Ícono del vehículo - según tipo/función
 */
@Injectable({
  providedIn: 'root',
})
export class VehicleIconGeneratorService {
  // Cache de imágenes cargadas
  private imageCache = new Map<string, HTMLImageElement>();

  // Cargas en vuelo por URL. Sin esto, N generaciones concurrentes de la misma
  // URL (ej: 200 vehículos con el ícono por defecto, o el mismo activo en varios
  // mapas) disparan N requests: el cache recién se puebla en el onload.
  private imageLoads = new Map<string, Promise<HTMLImageElement>>();

  // Cache de íconos generados (key = nombre único)
  // CAMBIADO: ImageBitmap → ImageData para mejor compatibilidad cross-browser
  // ImageData es más confiable con transparencias según Mapbox PR #5155
  //
  // LRU acotado: la key incluye orientación (buckets de 10°), color de anillo,
  // selección y (en modo foto) el activo, así que con la pestaña abierta horas
  // se acumulaban miles de ImageData de ~160 KB. Mapbox ya guarda su propia
  // copia de las imágenes en uso (map.hasImage); esto solo evita regenerar.
  private generatedIconsCache = new Map<string, ImageData>();
  private readonly MAX_GENERATED_ICONS = 500;

  // ============================================================================
  // TAMAÑOS DE REFERENCIA - NO MODIFICAR SIN ACTUALIZAR ESCALAS EN MAPBOX
  // ============================================================================
  // Estos tamaños definen el canvas base para generar íconos compuestos.
  // Los tamaños finales en pantalla se controlan con las escalas de Mapbox.
  //
  // CANVAS BASE:
  // - Normal: 200×200px
  // - Seleccionado: 240×240px (20% más grande)
  //
  // ELEMENTOS INTERNOS (proporcionales al canvas, factor 0.375):
  // - Círculo de selección: size * 0.25 de radio (solo cuando isSelected=true)
  // - Círculo central (background): size * 0.375 de radio
  // - Ícono del vehículo: size * 0.375
  //
  // TAMAÑOS FINALES EN PANTALLA (con escalas de Mapbox):
  // - Normal: 200px × 0.264 = 52.8px
  // - Seleccionado: 240px × 0.276 × 1.2(escala) = 79.5px
  //
  // Ver: mapa-individual.component.ts para escalas de Mapbox (icon-size)
  // ============================================================================
  private readonly BASE_SIZE = 200;
  private readonly SELECTION_SIZE = 240;

  constructor() {}

  /**
   * Pre-carga una imagen y la guarda en cache
   */
  private async loadImage(url: string): Promise<HTMLImageElement> {
    if (this.imageCache.has(url)) {
      return this.imageCache.get(url)!;
    }

    const enVuelo = this.imageLoads.get(url);
    if (enVuelo) {
      return enVuelo;
    }

    const carga = this.cargarImagen(url).finally(() =>
      this.imageLoads.delete(url),
    );
    this.imageLoads.set(url, carga);
    return carga;
  }

  private async cargarImagen(url: string): Promise<HTMLImageElement> {
    const defaultUrl = 'assets/map/vehiculos/Markers Default/Tipo/auto.png';

    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        this.imageCache.set(url, img);
        resolve(img);
      };
      img.onerror = () => {
        console.warn(
          `⚠️ No se pudo cargar ícono: ${url}, usando ícono por defecto`,
        );
        if (url === defaultUrl) {
          resolve(img);
          return;
        }
        this.loadImage(defaultUrl)
          .then(resolve)
          .catch(() => resolve(img));
      };
      img.src = url;
    });
  }

  /**
   * Genera un nombre único para el ícono basado en sus parámetros
   */
  generateIconKey(params: {
    iconUrl: string;
    backgroundColor: string;
    iconColor?: string;
    isSelected: boolean;
  }): string {
    const parts = params.iconUrl.split('/');
    const iconName = parts.pop()?.replace('.png', '') || 'default';
    const folder = parts.pop() || '';
    const bg = params.backgroundColor.replace('#', '');
    const icon = params.iconColor?.replace('#', '') || 'original';
    const sel = params.isSelected ? 'sel' : 'nosel';

    return `vehicle-${folder}-${iconName}-bg${bg}-icon${icon}-${sel}`;
  }

  /**
   * Key del ícono unificado (anillo bakeado + fondo + ícono tipo/función).
   * Se calcula sin generar nada: permite consultar el cache antes de decidir
   * si hay que pagar el canvas + carga de imagen.
   */
  mergedIconKey(params: {
    iconUrl: string;
    backgroundColor: string;
    iconColor?: string;
    isSelected: boolean;
    ringColor: string;
    orientation: number;
  }): string {
    const roundedAngle = Math.round(params.orientation / 10) * 10;
    const parts = params.iconUrl.split('/');
    const iconName = parts.pop()?.replace('.png', '') || 'default';
    const folder = parts.pop() || '';
    const bg = params.backgroundColor.replace('#', '');
    const icon = params.iconColor?.replace('#', '') || 'original';
    const ring = params.ringColor.replace('#', '');
    const sel = params.isSelected ? 'sel' : 'nosel';

    return `vehicle-merged-${folder}-${iconName}-bg${bg}-icon${icon}-ring${ring}-rot${roundedAngle}-${sel}`;
  }

  /**
   * Key del ícono unificado con foto del activo. Igual que mergedIconKey,
   * pero para el modo foto (centrado/completo).
   */
  mergedPhotoIconKey(params: {
    photoUrl: string;
    backgroundColor: string;
    isSelected: boolean;
    ringColor: string;
    orientation: number;
    modo: 'centrado' | 'completo';
  }): string {
    const roundedAngle = Math.round(params.orientation / 10) * 10;
    const urlPart =
      params.photoUrl.split('/').pop()?.split('.')[0]?.slice(-30) || 'photo';
    const bg = params.backgroundColor.replace('#', '');
    const ring = params.ringColor.replace('#', '');
    const sel = params.isSelected ? 'sel' : 'nosel';

    return `vehicle-photo-merged-${params.modo}-${urlPart}-bg${bg}-ring${ring}-rot${roundedAngle}-${sel}`;
  }

  /**
   * Indica si un ícono ya está generado en cache (consulta sincrónica).
   */
  hasCachedIcon(key: string): boolean {
    return this.generatedIconsCache.has(key);
  }

  /**
   * ImageData ya generado para una key, si está en cache (consulta sincrónica).
   */
  getCachedIcon(key: string): ImageData | undefined {
    return this.getGenerated(key);
  }

  /**
   * Calcula el color del anillo según velocidad e ignición
   */
  calculateRingColor(
    velocidad: number = 0,
    ignicion: boolean | undefined,
    vista: {
      offColor?: string;
      stopColor?: string;
      movingColor?: string;
      movingDegraded?: boolean;
    },
  ): string {
    let colorEstado = vista.offColor || '#616161'; // Apagado por defecto

    if (velocidad > 5) {
      // En movimiento (más de 5 km/h)
      if (vista.movingDegraded) {
        // Degradado: interpolar de amarillo a rojo según velocidad
        const colorAmarillo = '#f9e425'; // Amarillo para velocidad mínima (>5 km/h)
        const colorRojo = '#d82929'; // Rojo para velocidad máxima (>=120 km/h)

        if (velocidad >= 120) {
          colorEstado = colorRojo;
        } else {
          // Entre 5 y 120 km/h: interpolar
          const factor = (velocidad - 5) / (120 - 5);
          colorEstado = this.interpolateColor(colorAmarillo, colorRojo, factor);
        }
      } else {
        // Sin degradado: verde directo
        colorEstado = vista.movingColor || '#388e3c';
      }
    } else {
      // Velocidad <= 5 km/h, verificar ignición
      if (ignicion === true) {
        // Detenido (encendido pero sin movimiento) - Azul
        colorEstado = vista.stopColor || '#1976d2';
      } else {
        // Vehículo apagado
        colorEstado = vista.offColor || '#616161';
      }
    }

    return colorEstado;
  }

  /**
   * Interpola entre dos colores hexadecimales según un factor (0-1)
   */
  private interpolateColor(
    color1: string,
    color2: string,
    factor: number,
  ): string {
    factor = Math.max(0, Math.min(1, factor));

    const hex1 = color1.replace('#', '');
    const hex2 = color2.replace('#', '');

    const r1 = parseInt(hex1.substring(0, 2), 16);
    const g1 = parseInt(hex1.substring(2, 4), 16);
    const b1 = parseInt(hex1.substring(4, 6), 16);

    const r2 = parseInt(hex2.substring(0, 2), 16);
    const g2 = parseInt(hex2.substring(2, 4), 16);
    const b2 = parseInt(hex2.substring(4, 6), 16);

    const r = Math.round(r1 + (r2 - r1) * factor);
    const g = Math.round(g1 + (g2 - g1) * factor);
    const b = Math.round(b1 + (b2 - b1) * factor);

    const rHex = r.toString(16).padStart(2, '0');
    const gHex = g.toString(16).padStart(2, '0');
    const bHex = b.toString(16).padStart(2, '0');

    return `#${rHex}${gHex}${bHex}`;
  }

  /**
   * Aclara un color mezclándolo con blanco
   */
  private lightenColor(color: string, amount: number = 0.3): string {
    return this.interpolateColor(color, '#ffffff', amount);
  }

  /**
   * Aplica un color de tinte a una imagen usando canvas
   */
  private tintImage(
    img: HTMLImageElement,
    color: string,
    width: number,
    height: number,
  ): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', {
      alpha: true,
      willReadFrequently: false,
    })!;

    // Limpiar canvas para transparencia total
    ctx.clearRect(0, 0, width, height);

    // Dibujar imagen original
    ctx.drawImage(img, 0, 0, width, height);

    // Aplicar color usando composite
    ctx.globalCompositeOperation = 'source-in';
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, width, height);

    return canvas;
  }

  /**
   * Genera un ícono compuesto de vehículo (SIN anillo de orientación)
   *
   * Este método genera solo: Selection (si aplica) + Background + Ícono del vehículo
   * El anillo de orientación se maneja en una capa separada en Mapbox para permitir rotación independiente.
   *
   * CAMBIO: Ahora retorna ImageData en lugar de ImageBitmap para mejor compatibilidad
   * cross-browser con transparencias (especialmente Windows/DirectX/ANGLE).
   *
   * @param params Parámetros de configuración del ícono
   * @returns Promise con el ImageData del ícono generado
   */
  async generateVehicleIcon(params: {
    iconUrl: string;
    backgroundColor: string;
    iconColor?: string;
    isSelected: boolean;
  }): Promise<{ key: string; imageData: ImageData }> {
    const key = this.generateIconKey(params);

    // Verificar cache
    if (this.generatedIconsCache.has(key)) {
      return {
        key,
        imageData: this.getGenerated(key)!,
      };
    }

    // Crear canvas para composición
    const size = params.isSelected ? this.SELECTION_SIZE : this.BASE_SIZE;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;

    // OPCIÓN 1: Obtener contexto con alpha explícito y limpiar canvas
    const ctx = canvas.getContext('2d', {
      alpha: true, // Habilitar canal alfa explícitamente
      willReadFrequently: false, // Optimización: no vamos a leer píxeles
    })!;

    // Limpiar todo el canvas para asegurar transparencia total
    ctx.clearRect(0, 0, size, size);

    const centerX = size / 2;
    const centerY = size / 2;

    // CAPA 1: Círculo de selección (solo si está seleccionado)
    // Radio: 25% del canvas (proporcional)
    if (params.isSelected) {
      const selectionRadius = Math.round(size * 0.25);
      const selectionColor = this.lightenColor(params.backgroundColor, 0.4);

      ctx.fillStyle = selectionColor;
      ctx.beginPath();
      ctx.arc(centerX, centerY, selectionRadius, 0, Math.PI * 2);
      ctx.fill();
    }

    // CAPA 2: Fondo central (círculo de color de flota)
    const circleRadius = Math.round(size * 0.375); // 37.5% del canvas

    // Configurar antialiasing para bordes suaves
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    ctx.fillStyle = params.backgroundColor;
    ctx.beginPath();
    ctx.arc(centerX, centerY, circleRadius, 0, Math.PI * 2);
    ctx.fill();

    // CAPA 3: Ícono del vehículo (tipo o función)
    const iconSize = Math.round(size * 0.375); // 37.5% del canvas
    const vehicleIcon = await this.loadImage(params.iconUrl);

    let tintedIcon: HTMLCanvasElement;
    if (params.iconColor) {
      tintedIcon = this.tintImage(
        vehicleIcon,
        params.iconColor,
        iconSize,
        iconSize,
      );
    } else {
      // Sin tinte, usar imagen original
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = iconSize;
      tempCanvas.height = iconSize;
      const tempCtx = tempCanvas.getContext('2d', {
        alpha: true,
        willReadFrequently: false,
      })!;
      tempCtx.clearRect(0, 0, iconSize, iconSize);
      tempCtx.drawImage(vehicleIcon, 0, 0, iconSize, iconSize);
      tintedIcon = tempCanvas;
    }

    ctx.drawImage(tintedIcon, centerX - iconSize / 2, centerY - iconSize / 2);

    // SOLUCIÓN PARA TRANSPARENCIA: Usar ImageData en lugar de ImageBitmap
    // ImageData es más confiable cross-browser (especialmente Windows/ANGLE)
    // y Mapbox maneja correctamente la premultiplicación de alpha según PR #5155
    const imageData = ctx.getImageData(0, 0, size, size);

    // Guardar en cache
    this.setGenerated(key, imageData);

    return { key, imageData };
  }

  /**
   * Genera un ícono circular con la foto del activo (sin anillo de orientación).
   * Reemplaza el ícono tipo/función cuando mostrarFoto=true.
   */
  async generateVehiclePhotoIcon(params: {
    photoUrl: string;
    backgroundColor: string;
    isSelected: boolean;
    modo: 'centrado' | 'completo';
  }): Promise<{ key: string; imageData: ImageData }> {
    const urlPart =
      params.photoUrl.split('/').pop()?.split('.')[0]?.slice(-30) || 'photo';
    const bg = params.backgroundColor.replace('#', '');
    const sel = params.isSelected ? 'sel' : 'nosel';
    const key = `vehicle-photo-${params.modo}-${urlPart}-bg${bg}-${sel}`;

    if (this.generatedIconsCache.has(key)) {
      return { key, imageData: this.getGenerated(key)! };
    }

    const size = params.isSelected ? this.SELECTION_SIZE : this.BASE_SIZE;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d', {
      alpha: true,
      willReadFrequently: false,
    })!;
    ctx.clearRect(0, 0, size, size);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    const centerX = size / 2;
    const centerY = size / 2;

    if (params.isSelected) {
      const selectionRadius = Math.round(size * 0.25);
      const selectionColor = this.lightenColor(params.backgroundColor, 0.4);
      ctx.fillStyle = selectionColor;
      ctx.beginPath();
      ctx.arc(centerX, centerY, selectionRadius, 0, Math.PI * 2);
      ctx.fill();
    }

    const circleRadius = Math.round(size * 0.375);
    ctx.fillStyle = params.backgroundColor;
    ctx.beginPath();
    ctx.arc(centerX, centerY, circleRadius, 0, Math.PI * 2);
    ctx.fill();

    const photo = await this.loadImage(params.photoUrl);
    const photoRadius = Math.round(size * 0.35);
    const photoSize = photoRadius * 2;
    const nw = photo.naturalWidth || photoSize;
    const nh = photo.naturalHeight || photoSize;

    ctx.save();
    ctx.beginPath();
    ctx.arc(centerX, centerY, photoRadius, 0, Math.PI * 2);
    ctx.clip();

    if (params.modo === 'completo') {
      // Contain: imagen completa sin recortar, fondo oscuro en los bordes
      const scale = Math.min(photoSize / nw, photoSize / nh);
      const drawW = nw * scale;
      const drawH = nh * scale;
      ctx.fillStyle = 'rgba(20,20,20,0.85)';
      ctx.fillRect(
        centerX - photoRadius,
        centerY - photoRadius,
        photoSize,
        photoSize,
      );
      ctx.drawImage(
        photo,
        centerX - drawW / 2,
        centerY - drawH / 2,
        drawW,
        drawH,
      );
    } else {
      // Centrado (cover): recortar centrado para llenar el círculo
      const scale = Math.max(photoSize / nw, photoSize / nh);
      const drawW = nw * scale;
      const drawH = nh * scale;
      ctx.drawImage(
        photo,
        centerX - drawW / 2,
        centerY - drawH / 2,
        drawW,
        drawH,
      );
    }

    ctx.restore();

    const imageData = ctx.getImageData(0, 0, size, size);
    this.setGenerated(key, imageData);
    return { key, imageData };
  }

  /**
   * Genera un ícono unificado con foto circular + anillo bakeado.
   * Equivale a generateMergedVehicleIcon pero con foto en lugar de ícono tipo/función.
   */
  async generateMergedVehiclePhotoIcon(params: {
    photoUrl: string;
    backgroundColor: string;
    isSelected: boolean;
    ringColor: string;
    orientation: number;
    modo: 'centrado' | 'completo';
  }): Promise<{ key: string; imageData: ImageData }> {
    const roundedAngle = Math.round(params.orientation / 10) * 10;
    const key = this.mergedPhotoIconKey(params);

    if (this.generatedIconsCache.has(key)) {
      return { key, imageData: this.getGenerated(key)! };
    }

    const size = params.isSelected ? this.SELECTION_SIZE : this.BASE_SIZE;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d', {
      alpha: true,
      willReadFrequently: false,
    })!;
    ctx.clearRect(0, 0, size, size);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    const centerX = size / 2;
    const centerY = size / 2;

    if (params.isSelected) {
      const selectionRadius = Math.round(size * 0.25);
      const selectionColor = this.lightenColor(params.backgroundColor, 0.4);
      ctx.fillStyle = selectionColor;
      ctx.beginPath();
      ctx.arc(centerX, centerY, selectionRadius, 0, Math.PI * 2);
      ctx.fill();
    }

    const circleRadius = Math.round(size * 0.375);
    ctx.fillStyle = params.backgroundColor;
    ctx.beginPath();
    ctx.arc(centerX, centerY, circleRadius, 0, Math.PI * 2);
    ctx.fill();

    // Anillo de orientación bakeado
    const margin = size * 0.125;
    const outerRadius = size / 2 - margin;
    const ringWidth = size * 0.095;

    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.rotate((roundedAngle * Math.PI) / 180);
    ctx.translate(-centerX, -centerY);

    ctx.strokeStyle = params.ringColor;
    ctx.lineWidth = ringWidth;
    ctx.beginPath();
    ctx.arc(centerX, centerY, outerRadius - ringWidth / 2, 0, Math.PI * 2);
    ctx.stroke();

    const ringOuterEdge = centerY - outerRadius;
    const arrowWidth = size * 0.28;
    const arrowTip = Math.round(size * 0.02);
    const arrowBase = ringOuterEdge + ringWidth * 0.4;
    ctx.fillStyle = params.ringColor;
    ctx.beginPath();
    ctx.moveTo(centerX, arrowTip);
    ctx.lineTo(centerX + arrowWidth / 2, arrowBase);
    ctx.lineTo(centerX - arrowWidth / 2, arrowBase);
    ctx.closePath();
    ctx.fill();

    ctx.restore();

    // Foto circular encima del fondo
    const photo = await this.loadImage(params.photoUrl);
    const photoRadius = Math.round(size * 0.35);
    const photoSize = photoRadius * 2;
    const nw = photo.naturalWidth || photoSize;
    const nh = photo.naturalHeight || photoSize;

    ctx.save();
    ctx.beginPath();
    ctx.arc(centerX, centerY, photoRadius, 0, Math.PI * 2);
    ctx.clip();

    if (params.modo === 'completo') {
      const scale = Math.min(photoSize / nw, photoSize / nh);
      const drawW = nw * scale;
      const drawH = nh * scale;
      ctx.fillStyle = 'rgba(20,20,20,0.85)';
      ctx.fillRect(
        centerX - photoRadius,
        centerY - photoRadius,
        photoSize,
        photoSize,
      );
      ctx.drawImage(
        photo,
        centerX - drawW / 2,
        centerY - drawH / 2,
        drawW,
        drawH,
      );
    } else {
      const scale = Math.max(photoSize / nw, photoSize / nh);
      const drawW = nw * scale;
      const drawH = nh * scale;
      ctx.drawImage(
        photo,
        centerX - drawW / 2,
        centerY - drawH / 2,
        drawW,
        drawH,
      );
    }

    ctx.restore();

    const imageData = ctx.getImageData(0, 0, size, size);
    this.setGenerated(key, imageData);
    return { key, imageData };
  }

  // Lectura del cache marcando la entrada como usada recientemente (LRU).
  private getGenerated(key: string): ImageData | undefined {
    const imageData = this.generatedIconsCache.get(key);
    if (imageData) {
      this.generatedIconsCache.delete(key);
      this.generatedIconsCache.set(key, imageData);
    }
    return imageData;
  }

  // Escritura del cache descartando las entradas menos usadas si se pasa del tope.
  private setGenerated(key: string, imageData: ImageData): void {
    this.generatedIconsCache.delete(key);
    this.generatedIconsCache.set(key, imageData);
    while (this.generatedIconsCache.size > this.MAX_GENERATED_ICONS) {
      const oldest = this.generatedIconsCache.keys().next().value;
      if (oldest === undefined) break;
      this.generatedIconsCache.delete(oldest);
    }
  }

  /**
   * Limpia el cache de íconos generados
   */
  clearCache(): void {
    // ImageData no requiere liberación manual (a diferencia de ImageBitmap)
    this.generatedIconsCache.clear();
    this.imageCache.clear();
    this.imageLoads.clear();
  }

  /**
   * Limpia íconos del cache que no estén en el conjunto de keys utilizadas
   * Útil para evitar memory leaks con configuraciones dinámicas
   *
   * @param usedKeys Set de keys que están actualmente en uso
   */
  clearUnusedCache(usedKeys: Set<string>): void {
    const toDelete: string[] = [];

    for (const [key] of this.generatedIconsCache.entries()) {
      if (!usedKeys.has(key)) {
        toDelete.push(key);
      }
    }

    // Eliminar del cache (ImageData no requiere liberación manual)
    for (const key of toDelete) {
      this.generatedIconsCache.delete(key);
    }
  }

  /**
   * Obtiene el tamaño del cache
   */
  getCacheSize(): number {
    return this.generatedIconsCache.size;
  }

  /**
   * Obtiene estadísticas del cache para debugging
   */
  getCacheStats(): {
    generatedIconsCount: number;
    loadedImagesCount: number;
    cacheKeys: string[];
  } {
    return {
      generatedIconsCount: this.generatedIconsCache.size,
      loadedImagesCount: this.imageCache.size,
      cacheKeys: Array.from(this.generatedIconsCache.keys()),
    };
  }

  /**
   * Convierte ImageData a data URI (PNG base64)
   * Útil para usar el canvas generado como backgroundImage en HTML markers
   *
   * @param imageData ImageData del canvas
   * @returns Data URI en formato 'data:image/png;base64,...'
   */
  imageDataToDataUrl(imageData: ImageData): string {
    const canvas = document.createElement('canvas');
    canvas.width = imageData.width;
    canvas.height = imageData.height;
    const ctx = canvas.getContext('2d')!;
    ctx.putImageData(imageData, 0, 0);
    return canvas.toDataURL('image/png');
  }

  /**
   * Genera un ícono compuesto unificado: anillo rotado (bakeado) + fondo + ícono del vehículo.
   *
   * A diferencia de generateVehicleIcon (que omite el anillo), este método integra
   * el anillo de orientación directamente en el canvas usando transformaciones de contexto,
   * permitiendo usar una sola capa Mapbox por set de markers y evitar problemas de z-ordering
   * entre markers superpuestos.
   *
   * Técnica: ctx.save/rotate → dibuja anillo apuntando "arriba" → ctx.restore → dibuja fondo+ícono derechos.
   * La orientación se hornea con el ángulo efectivo = vehicleOrientation - mapBearing.
   */
  async generateMergedVehicleIcon(params: {
    iconUrl: string;
    backgroundColor: string;
    iconColor?: string;
    isSelected: boolean;
    ringColor: string;
    orientation: number; // ángulo efectivo en grados (compensado por mapBearing)
  }): Promise<{ key: string; imageData: ImageData }> {
    // Redondear a 10° para limitar el tamaño del cache
    const roundedAngle = Math.round(params.orientation / 10) * 10;
    const key = this.mergedIconKey(params);

    if (this.generatedIconsCache.has(key)) {
      return { key, imageData: this.getGenerated(key)! };
    }

    const size = params.isSelected ? this.SELECTION_SIZE : this.BASE_SIZE;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;

    const ctx = canvas.getContext('2d', {
      alpha: true,
      willReadFrequently: false,
    })!;
    ctx.clearRect(0, 0, size, size);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    const centerX = size / 2;
    const centerY = size / 2;

    // CAPA 1: Círculo de selección (solo si está seleccionado)
    if (params.isSelected) {
      const selectionRadius = Math.round(size * 0.25);
      const selectionColor = this.lightenColor(params.backgroundColor, 0.4);
      ctx.fillStyle = selectionColor;
      ctx.beginPath();
      ctx.arc(centerX, centerY, selectionRadius, 0, Math.PI * 2);
      ctx.fill();
    }

    // CAPA 2: Fondo central (siempre derecho, se dibuja ANTES del anillo)
    const circleRadius = Math.round(size * 0.375);
    ctx.fillStyle = params.backgroundColor;
    ctx.beginPath();
    ctx.arc(centerX, centerY, circleRadius, 0, Math.PI * 2);
    ctx.fill();

    // CAPA 3: Anillo de orientación bakeado al ángulo efectivo (encima del fondo)
    // Usamos ctx.save/rotate para rotar solo el anillo, luego ctx.restore
    // para que el ícono quede siempre derecho (viewport-aligned)
    const margin = size * 0.125;
    const outerRadius = size / 2 - margin;
    const ringWidth = size * 0.095;

    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.rotate((roundedAngle * Math.PI) / 180);
    ctx.translate(-centerX, -centerY);

    // Anillo circular
    ctx.strokeStyle = params.ringColor;
    ctx.lineWidth = ringWidth;
    ctx.beginPath();
    ctx.arc(centerX, centerY, outerRadius - ringWidth / 2, 0, Math.PI * 2);
    ctx.stroke();

    // Flecha triangular (apunta hacia arriba en el canvas rotado = hacia el heading)
    const ringOuterEdge = centerY - outerRadius;
    const arrowWidth = size * 0.28;
    const arrowTip = Math.round(size * 0.02); // ~2% del canvas desde el borde superior
    const arrowBase = ringOuterEdge + ringWidth * 0.4;
    ctx.fillStyle = params.ringColor;
    ctx.beginPath();
    ctx.moveTo(centerX, arrowTip);
    ctx.lineTo(centerX + arrowWidth / 2, arrowBase);
    ctx.lineTo(centerX - arrowWidth / 2, arrowBase);
    ctx.closePath();
    ctx.fill();

    ctx.restore();

    // CAPA 4: Ícono del vehículo (siempre derecho)
    const iconSize = Math.round(size * 0.375);
    const vehicleIcon = await this.loadImage(params.iconUrl);

    let tintedIcon: HTMLCanvasElement;
    if (params.iconColor) {
      tintedIcon = this.tintImage(
        vehicleIcon,
        params.iconColor,
        iconSize,
        iconSize,
      );
    } else {
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = iconSize;
      tempCanvas.height = iconSize;
      const tempCtx = tempCanvas.getContext('2d', {
        alpha: true,
        willReadFrequently: false,
      })!;
      tempCtx.clearRect(0, 0, iconSize, iconSize);
      tempCtx.drawImage(vehicleIcon, 0, 0, iconSize, iconSize);
      tintedIcon = tempCanvas;
    }
    ctx.drawImage(tintedIcon, centerX - iconSize / 2, centerY - iconSize / 2);

    const imageData = ctx.getImageData(0, 0, size, size);
    this.setGenerated(key, imageData);

    return { key, imageData };
  }

  /**
   * Genera un anillo de orientación dinámicamente con Canvas (sin SDF).
   *
   * SOLUCIÓN para artefactos de transparencia:
   * En lugar de usar PNG con SDF + icon-color (causa recuadros grises),
   * generamos el anillo dinámicamente con Canvas en el color exacto sin SDF.
   *
   * @param color Color del anillo en formato hex
   * @param size Tamaño del canvas (800 normal, 960 seleccionado)
   * @param isSelected Si el marker está seleccionado
   * @returns ImageData del anillo generado
   */
  generateOrientationRing(
    color: string,
    size: number = 800,
    isSelected: boolean = false,
  ): ImageData {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;

    const ctx = canvas.getContext('2d', {
      alpha: true,
      willReadFrequently: false,
    })!;

    // Limpiar canvas para transparencia total
    ctx.clearRect(0, 0, size, size);

    const centerX = size / 2;
    const centerY = size / 2;

    // Calcular dimensiones proporcionales al tamaño del canvas
    // AUMENTAR MARGEN para dar espacio a la flecha FUERA del anillo
    const margin = size * 0.125; // ~100px en 800px, ~120px en 960px (mucho más espacio)
    const outerRadius = size / 2 - margin; // Radio exterior del anillo
    const ringWidth = size * 0.095; // ~76px en 800px, ~91px en 960px (más angosto)

    // Configurar antialiasing para bordes suaves
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // DIBUJAR ANILLO CIRCULAR (GRUESO como en la referencia)
    ctx.strokeStyle = color;
    ctx.lineWidth = ringWidth;
    ctx.beginPath();
    ctx.arc(centerX, centerY, outerRadius - ringWidth / 2, 0, Math.PI * 2);
    ctx.stroke();

    // DIBUJAR FLECHA TRIANGULAR FUERA del anillo (apuntando hacia arriba)
    // CRÍTICO: La flecha debe estar COMPLETAMENTE FUERA del anillo, no superpuesta
    // Según el PNG de referencia, la flecha está claramente separada del anillo

    const ringOuterEdge = centerY - outerRadius; // Borde exterior del anillo (Y coordinate)

    // Dimensiones de la flecha (MUY GRANDE y superpuesta con el anillo)
    const arrowHeight = margin * 1.15; // ~115px en 800px - más alta que el margen
    const arrowWidth = size * 0.28; // ~224px de ancho (más ancha)

    // Posicionar la flecha metida parcialmente en el anillo:
    // - Punta en el tope del canvas (Y = 10-15px, dejando mínimo margen)
    // - Base DENTRO del anillo para ocultar bordes por curvatura y dar continuidad visual
    const arrowTip = 15; // Pequeño margen desde el borde superior
    const arrowBase = ringOuterEdge + ringWidth * 0.4; // Base metida ~40% dentro del grosor del anillo

    const arrowLeft = centerX - arrowWidth / 2;
    const arrowRight = centerX + arrowWidth / 2;

    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(centerX, arrowTip); // Punta superior (cerca del borde del canvas)
    ctx.lineTo(arrowRight, arrowBase); // Esquina inferior derecha (antes del anillo)
    ctx.lineTo(arrowLeft, arrowBase); // Esquina inferior izquierda (antes del anillo)
    ctx.closePath();
    ctx.fill();

    // Retornar ImageData
    const imageData = ctx.getImageData(0, 0, size, size);

    return imageData;
  }
}
